<script lang="ts" setup>
import { CalendarDate, getDayOfWeek, parseDate, parseTime, Time } from "@internationalized/date"
import { Location } from "@mmh/types"
import { InquiryType, StyleOptions } from "@mmh/types/constants"
import { toTypedSchema } from "@vee-validate/zod"
import { createReusableTemplate } from "@vueuse/core"
import { useRouteQuery } from "@vueuse/router"
import { CalendarIcon, ChevronDown, Clock, UsersRound, Utensils, X } from "lucide-vue-next"
import { configure, useForm } from "vee-validate"
import { useSlots, type HTMLAttributes } from "vue"
import { RouteLocationRaw } from "vue-router"
import { toast } from "vue-sonner"
import { z } from "zod"
import { ThemeButton, ThemeInput, ThemeTextarea } from "~/components/theme"
import { Badge } from "~/components/ui/badge"
import { buttonVariants } from "~/components/ui/button"
import { Calendar } from "~/components/ui/calendar"
import { Checkbox } from "~/components/ui/checkbox"
import { Collapsible, CollapsibleContent } from "~/components/ui/collapsible"
import { Dialog, DialogContent, DialogTrigger } from "~/components/ui/dialog"
import { Drawer, DrawerClose, DrawerContent, DrawerTrigger } from "~/components/ui/drawer"
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "~/components/ui/form"
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover"
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select"
import { Separator } from "~/components/ui/separator"
import { useCreateLead } from "~/composables/api"
import { useConcept } from "~/composables/concept"
import { useLocation } from "~/composables/locations"
import { useMedia } from "~/composables/media"
import { useServiceFiltering } from "~/composables/serviceFiltering"
import { useTime } from "~/composables/time"
import { TriggerType, useInquiryAnalytics } from "~/composables/useInquiryAnalytics"
import { specialCase, titleCase } from "~/lib/case"
import { maskPhone } from "~/lib/mask"
import { cn } from "~/lib/utils"
import { emailValidator, phoneValidator } from "~/lib/validate"
import { open as openUrl } from "~/lib/window"

configure({
    validateOnChange: true,
    validateOnInput: true,
    validateOnModelUpdate: true,
    validateOnBlur: false
})

const emit = defineEmits<{
    (e: "success"): void,
    (e: "cancel"): void
}>()

const { location, guests, date, time, type, class: cl, triggerType } = defineProps<{
    type: typeof InquiryType[keyof typeof InquiryType]
    location: Location
    guests?: number
    date?: CalendarDate
    time?: Time
    to?: RouteLocationRaw
    class?: HTMLAttributes["class"]
    triggerType?: TriggerType
}>()

const inquiryAnalytics = useInquiryAnalytics()
const slots = useSlots()

const open = defineModel("open", { type: Boolean, default: false, required: false })

const modal = useRouteQuery("modal")

const openedViaButton = ref(false)

watch(open, (value) => {
    if (value === false) {
        if (inquiryAnalytics.hasStarted.value && !inquiryAnalytics.hasSubmitted.value) {
            inquiryAnalytics.trackFormAbandoned(form.values as Record<string, unknown>, "modal_close")
        }
        inquiryAnalytics.cleanupAbandonmentTracking()
        inquiryAnalytics.reset()
        openedViaButton.value = false
        emit("cancel")
        form.resetForm()
    } else {
        const trigger: TriggerType = triggerType && (openedViaButton.value || !slots.default) ? triggerType : "direct"
        inquiryAnalytics.trackFormOpened(trigger)
        inquiryAnalytics.setupAbandonmentTracking(() => form.values as Record<string, unknown>)
        
        form.resetForm({
            values: {
                guests: guests || undefined,
                date: date ? date.toString() : undefined,
                start_time: time ? time.toString() : undefined,
                duration: durationDefault,
                style: styleDefault.value,
                description: ""
            }
        })
    }
})

const modalQueryMap = {
    [InquiryType.LargeParty]: "large-party-inquiry-form",
    [InquiryType.PrivateDining]: "private-dining-inquiry-form",
    [InquiryType.Catering]: "catering-inquiry-form"
}

watch(modal, value => {
    if (value === modalQueryMap[type]) {
        open.value = true
    }
}, { immediate: true })

watch(open, value => {
    if (!value) {
        if (modal.value === modalQueryMap[type]) {
            modal.value = undefined
        }
    }
})

const { isDesktop } = useMedia()

const { concept } = useConcept()
const { hasOneLocation } = useLocation()
const { currentDate, currentTime, formatDate, formatTime, isDateDisabled: _isDateDisabled } = useTime("America/Los_Angeles")

const isDateDisabled = (date: any) => {
    if (_isDateDisabled(date)) return true
    return concept.value?.disabledDates?.inquiry?.some(
        (d: { month: number; day: number }) => d.month === date.month && d.day === date.day
    ) ?? false
}

const { createLead, createdLead, createLeadError, createLeadLoading } = useCreateLead()

const guestLimit = 60
const calendarRange = 31
const durationDefault = 120
const styleDefault = StyleOptions[0]

const calendarOpen = ref(false)
const locationFullName = computed(() => {
    if (hasOneLocation.value) {
        return titleCase(concept.value.name)
    }
    return `${titleCase(concept.value.name)} ${titleCase(location.name)}`
})
const title = computed(() => `${titleCase(type)} Inquiry`)
const description = computed(() => {
    if (type ===InquiryType.Catering) {
        return `Request catering from ${locationFullName.value}`
    }
    return `We are delighted to host your party at ${locationFullName.value}!`
})
const needToKnow = computed(() => {
    switch (type) {
        case InquiryType.Catering: {
            return location.needToKnow?.catering
        }
        case InquiryType.LargeParty: {
            if (prixFixeRequired.value) {
                return location.needToKnow?.largePartyPrixFixe
            }
            return location.needToKnow?.largeParty
        }
        case InquiryType.PrivateDining: {
            if (prixFixeRequired.value) {
                return location.needToKnow?.privateDiningPrixFixe
            }
            return location.needToKnow?.privateDining
        }
    }
})

const descriptionLimit = 250
const nameLimit = 50
const extensionLimit = 10
const companyLimit = 100

const spaceOptions = computed(() => {
    const options = []
    if (type === InquiryType.PrivateDining) {
        for (const space of location.spaces || []) {
            options.push(space)
        }
    } else if (type === InquiryType.LargeParty) {
        for (const space of location.spaces || []) {
            if (!space.minGuests) continue
            if (space.maxGuests && selectedGuests.value && selectedGuests.value >= space.minGuests && selectedGuests.value <= space.maxGuests) {
                options.push(space)
            }
        }
    }
    return options
})

const formSchema = toTypedSchema(z.object({
    guests: z.number({ required_error: "Party size required" }).max(guestLimit),
    date: z.string({ required_error: "Date required" }),
    start_time: z.string({ required_error: "Start time required" }),
    private: z.boolean().optional(),
    space: type === InquiryType.PrivateDining && spaceOptions.value.length > 0
        ? z.string().min(1, "Space selection required")
        : z.string().optional().or(z.literal("")),
    duration: z.number({ required_error: "Duration required" }),
    style: z.enum([StyleOptions[0].value, StyleOptions[1].value]),
    description: z.string().max(descriptionLimit).optional(),
    firstName: z.string({ required_error: "First name required" }).min(1, "First name cannot be blank").max(nameLimit),
    lastName: z.string({ required_error: "Last name required" }).min(1, "Last name cannot be blank").max(nameLimit),
    email: emailValidator(),
    phone: phoneValidator(),
    extension: z.string().max(extensionLimit).optional(),
    company: z.string().max(companyLimit).optional(),
    gdpr: z.boolean({ required_error: "Agreement required" }).refine(value => value, { message: "Please accept the privacy policy" }),
    inquiryAcknowledgement: z.boolean({ required_error: "Acknowledgement required" }).refine(value => value, { message: "Please acknowledge the inquiry terms" })
}))

const form = useForm({ 
    validationSchema: formSchema,
    initialValues: {
        guests: guests || undefined,
        date: date ? date.toString() : undefined,
        start_time: time ? time.toString() : undefined,
        duration: durationDefault,
        style: styleDefault.value,
        description: ""
    }
})

const selectedGuests = computed(() => form.values.guests ?? guests)
const selectedDate = computed(() => form.values.date ? parseDate(form.values.date) : date)
const selectedTime = computed(() => form.values.start_time ? parseTime(form.values.start_time) : time)
const selectedSpace = computed(() => spaceOptions.value.find(o => o.title === form.values.space))
const selectedDay = computed(() => selectedDate.value ? getDayOfWeek(selectedDate.value, "en-US") : undefined)
const selectedOpenClose = computed((): [Time, Time] | null => {
    if (!location || selectedDay.value === undefined) return null

    const bookableServices = location.services.filter(service => service.bookable !== false)
    const timeRanges = []
    console.log(bookableServices)
    for (const service of bookableServices) {
        for (const hours of service?.hours || []) {
            if (hours.days.includes(selectedDay.value)) {
                let start = parseTime(hours.times[0])
                const end = parseTime(hours.times[1])
                if (
                    selectedDate.value &&
                    currentDate.value &&
                    selectedDate.value.compare(currentDate.value) === 0 &&
                    start.compare(currentTime.value) <= 0
                ) {
                    if (end.compare(currentTime.value) > 0) {
                        start = currentTime.value
                        timeRanges.push({ start, end })
                    }
                } else {
                    timeRanges.push({ start, end })
                }
            }
        }
    }

    if (timeRanges.length === 0) return null

    const earliestStart = timeRanges.reduce(
        (earliest, range) => (range.start.compare(earliest) < 0 ? range.start : earliest),
        timeRanges[0].start
    )

    const latestEnd = timeRanges.reduce(
        (latest, range) => (range.end.compare(latest) > 0 ? range.end : latest),
        timeRanges[0].end
    )

    return [earliestStart, latestEnd]
})
const selectedService = computed(() => selectedDay.value !== undefined && selectedTime.value ? location.services.filter(s => s.bookable !== false).find(s => s.hours?.some(h => h.days.includes(selectedDay.value!) && selectedTime.value!.compare(parseTime(h.times[0])) >= 0 && selectedTime.value!.compare(parseTime(h.times[1])) < 0)) : undefined)

const selectedServiceMenu = computed(() => {
    if (selectedService.value) {
        const largePartyMenuKey = selectedService.value.largePartyMenuKey
        const dailyMenuKey = selectedService.value.dailyMenuKey

        if (prixFixeRequired.value && selectedService.value.prixFixeThreshold) {
            return location.menus["large-party"].find(m => m.key === largePartyMenuKey)
        }
        return location.menus["daily"].find(m => m.key === dailyMenuKey)
    }
})
const selectedServiceMenuMessage = computed(() => {
    if (selectedService.value) {
        const serviceName = selectedService.value.name === "breakfast" ? "brunch" : selectedService.value.name
        return `${titleCase(serviceName)} parties of ${selectedService.value.prixFixeThreshold || 0} or more require a prix fixe menu.`
    }
})

function formatGuestCount(space: { minGuests?: number; maxGuests?: number | null }) {
    const { minGuests, maxGuests } = space
    
    if (minGuests && maxGuests) {
        return `${minGuests} - ${maxGuests} Guests`
    }

    if (maxGuests && !minGuests) {
        return `Up to ${maxGuests} Guests`
    }

    if (minGuests && !maxGuests) {
        return `${minGuests}+ Guests`
    }
    
    if (maxGuests === Infinity) {
        return minGuests ? `${minGuests}+ Guests` : ""
    }

    return ""
}

const resyThreshold = computed(() => location.services.find(s => s.key === "dinner")?.prixFixeThreshold)
const prixFixeRequired = computed(() => selectedService.value && selectedGuests.value && selectedService.value.prixFixeThreshold && selectedGuests.value >= selectedService.value.prixFixeThreshold)
const callRestaurantMessage = computed(() => {
    if (!selectedGuests.value) return ""
    if (!prixFixeRequired.value && selectedService.value) {
        return `
                ${titleCase(selectedService.value.name)} parties of ${selectedService.value.prixFixeThreshold || 0} or more require a prix fixe menu. 
                ${titleCase(selectedService.value.name)} parties of ${resyThreshold.value}-${(selectedService.value.prixFixeThreshold || 0) - 1} require a confirmed reservation. 
                Please call ${locationFullName.value} at ${location.contact.phone} to reserve your table ahead of your desired booking time.
            `
    } else {
        return ""
    }
})

const guestOptions = computed(() => {
    const options = []
    if (resyThreshold.value) {
        for (let i = resyThreshold.value; i <= guestLimit; i++) {
            options.push(i)
        }
    }
    return options
})

const locationRef = computed(() => location as Location)
const { timeOptions, calendarMinDate } = useServiceFiltering({
    location: locationRef,
    currentDate,
    currentTime,
    selectedDate,
    selectedDay,
    selectedOpenClose
})

watch(selectedGuests, (value) => {
    if (value) {
        if (selectedSpace.value && value > (selectedSpace.value.maxGuests || 0)) {
            form.resetField("space", { value: "" })
            if (type === InquiryType.PrivateDining) {
                form.validateField("space")
            }
        }
    }
}, { immediate: true })

watch(selectedDate, (value) => {
    if (value && value.compare(currentDate.value) === 0) {
        form.resetField("start_time", { value: "" })
    }
})

watch(guestOptions, (value) => {
    if (value.length && selectedGuests.value) {
        const first = value[0]
        const last = value[value.length - 1]
        if (selectedGuests.value < first) {
            form.resetField("guests", { value: first })
        } else if (selectedGuests.value > last) {
            form.resetField("guests", { value: last })
        }
    }
}, { immediate: true })

function handleInputPhone(ev: InputEvent) {
    const value = (ev.target as HTMLInputElement).value
    if (value) {
        form.setFieldValue("phone", maskPhone(value), !!form.errors.value["phone"])
    }
}

function handleInputExtension(ev: InputEvent) {
    const value = (ev.target as HTMLInputElement).value
    if (value) {
        form.setFieldValue("extension", value.replace(/[^0-9]/g, ""), !!form.errors.value["extension"])
    }
}

const handleSubmit = form.handleSubmit(async (values) => {
    const { start_time, duration, space, style, ...common } = values
    
    const apiPayload = {
        ...common,
        start_time: parseTime(start_time).toString()
    }
    const end_time = parseTime(start_time).add({ minutes: duration }).toString()
    
    const publicKey = location.external?.api?.tripleSeat?.publicKey
    if (!publicKey) return
    
    switch (type) {
        case InquiryType.Catering: {
            const formId = location.external?.api?.tripleSeat?.cateringId
            if (!formId) return
            return await createLead({
                type,
                publicKey,
                formId,
                ...apiPayload,
                style
            })
        }
        case InquiryType.LargeParty: {
            const formId = location.external?.api?.tripleSeat?.largePartyId
            if (!formId) return
            return await createLead({
                type,
                publicKey,
                formId,
                ...apiPayload,
                end_time,
                space: space || ""
            })
        }
        case InquiryType.PrivateDining: {
            const formId = location.external?.api?.tripleSeat?.privateDiningId
            if (!formId) return
            return await createLead({
                type,
                publicKey,
                formId,
                ...apiPayload,
                space: space || ""
            })
        }
    }
})

watch(createdLead, (value) => {
    if (value && open.value && !inquiryAnalytics.hasSubmitted.value) {
        inquiryAnalytics.trackFormSubmitted()
        form.resetForm()
    }
})

watch(createLeadError, (value) => {
    if (value) {
        toast.error(value.message, { 
            duration: Infinity, 
            closeButton: true
        })
    }
})

const [InquiryFormTemplate, InquiryForm] = createReusableTemplate()
</script>

<template>
    <InquiryFormTemplate class="w-full flex flex-col gap-10 md:gap-12">
        <div class="w-full space-y-8">
            <header
                v-if="!inquiryAnalytics.hasSubmitted.value"
                class="space-y-4 text-center"
            >
                <h3 class="text-xl md:text-2xl text-inquiry-form-title font-headline">
                    {{ title }}
                </h3>
                <Separator class="w-10 mx-auto bg-inquiry-form-title" />
                <p class="text-inquiry-form-text">
                    {{ description }}
                </p>
            </header>
            <form
                v-if="!inquiryAnalytics.hasSubmitted.value"
                class="flex flex-col items-center justify-center gap-8 md:gap-10"
                @submit="handleSubmit"
            >
                <div class="w-full grid md:grid-cols-2 gap-x-2 gap-y-8 md:gap-y-10">
                    <FormField
                        v-slot="{ componentField }"
                        name="firstName"
                    >
                        <FormItem>
                            <FormLabel class="uppercase text-inquiry-form-text">
                                First Name <b>*</b>
                            </FormLabel>
                            <FormControl>
                                <ThemeInput
                                    :maxlength="nameLimit"
                                    type="text"
                                    class="bg-inquiry-form-item-background text-inquiry-form-text [&::placeholder]:text-inquiry-form-placeholder"
                                    v-bind="componentField"
                                    @focus="inquiryAnalytics.trackFieldFocus('firstName')"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField
                        v-slot="{ componentField }"
                        name="lastName"
                    >
                        <FormItem>
                            <FormLabel class="uppercase text-inquiry-form-text">
                                Last Name <b>*</b>
                            </FormLabel>
                            <FormControl>
                                <ThemeInput
                                    :maxlength="nameLimit"
                                    type="text"
                                    class="bg-inquiry-form-item-background text-inquiry-form-text [&::placeholder]:text-inquiry-form-placeholder"
                                    v-bind="componentField"
                                    @focus="inquiryAnalytics.trackFieldFocus('lastName')"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <FormField
                        v-slot="{ componentField }"
                        name="email"
                    >
                        <FormItem>
                            <FormLabel class="uppercase text-inquiry-form-text">
                                Email <b>*</b>
                            </FormLabel>
                            <FormControl>
                                <ThemeInput
                                    type="text"
                                    autocapitalize="off"
                                    class="bg-inquiry-form-item-background text-inquiry-form-text [&::placeholder]:text-inquiry-form-placeholder"
                                    v-bind="componentField"
                                    @focus="inquiryAnalytics.trackFieldFocus('email')"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                    <div class="grid grid-cols-5 gap-2">
                        <FormField
                            v-slot="{ componentField }"
                            name="phone"
                        >
                            <FormItem class="col-span-3">
                                <FormLabel class="uppercase text-inquiry-form-text">
                                    Phone <b>*</b>
                                </FormLabel>
                                <FormControl>
                                    <ThemeInput
                                        type="tel"
                                        class="bg-inquiry-form-item-background text-inquiry-form-text [&::placeholder]:text-inquiry-form-placeholder"
                                        v-bind="componentField"
                                        @focus="inquiryAnalytics.trackFieldFocus('phone')"
                                        @input="handleInputPhone"
                                        @change="handleInputPhone"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField
                            v-slot="{ componentField }"
                            name="extension"
                        >
                            <FormItem class="col-span-2">
                                <FormLabel class="uppercase text-inquiry-form-text">
                                    Extension
                                </FormLabel>
                                <FormControl>
                                    <ThemeInput
                                        :maxlength="extensionLimit"
                                        type="text"
                                        inputmode="numeric"
                                        class="bg-inquiry-form-item-background text-inquiry-form-text [&::placeholder]:text-inquiry-form-placeholder [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" 
                                        v-bind="componentField"
                                        @focus="inquiryAnalytics.trackFieldFocus('extension')"
                                        @input="handleInputExtension"
                                        @change="handleInputExtension"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                    </div>
                    <FormField
                        v-slot="{ componentField }"
                        name="company"
                    >
                        <FormItem class="md:col-span-2">
                            <FormLabel class="uppercase text-inquiry-form-text">
                                Company
                            </FormLabel>
                            <FormControl>
                                <ThemeInput
                                    :maxlength="companyLimit"
                                    type="text"
                                    class="bg-inquiry-form-item-background text-inquiry-form-text [&::placeholder]:text-inquiry-form-placeholder"
                                    v-bind="componentField"
                                    @focus="inquiryAnalytics.trackFieldFocus('company')"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <div class="w-full grid md:grid-cols-3 gap-2">
                    <FormField
                        v-slot="{ value, handleChange }"
                        name="guests"
                    >
                        <FormItem class="space-y-0 [&>svg]:hidden inline-flex w-full flex-col">
                            <Select
                                :model-value="value?.toString()"
                                @update:model-value="handleChange(parseInt($event as string))"
                                @update:open="(open: boolean) => open && inquiryAnalytics.trackFieldFocus('guests')"
                            >
                                <FormControl>
                                    <SelectTrigger
                                        class="border-none focus:ring-0 focus:ring-offset-0"
                                        as-child
                                    >
                                        <ThemeButton
                                            size="lg"
                                            class="px-3 md:px-0 h-11 justify-between gap-4 md:gap-2 bg-inquiry-form-item-background text-inquiry-form-text hover:bg-inquiry-form-item-hover"
                                        >
                                            <div class="w-full flex items-center gap-4 md:gap-3">
                                                <UsersRound class="size-4 text-inquiry-form-text" />
                                                <SelectValue
                                                    placeholder="Select Party Size"
                                                    class="data-[placeholder]:text-inquiry-form-placeholder capsize"
                                                />
                                            </div>
                                            <ChevronDown class="size-4 text-inquiry-form-text/50" />
                                        </ThemeButton>
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent class="max-h-64 border-none">
                                    <SelectGroup>
                                        <SelectItem
                                            v-for="o of guestOptions"
                                            :key="o.toString()"
                                            :value="o.toString()"
                                        >
                                            {{ o }}{{ o === guestLimit ? "+" : "" }} {{ o === 1 ? "Guest" : "Guests" }}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <FormMessage class="w-full text-center" />
                        </FormItem>
                    </FormField>
                    <FormField
                        v-slot="{ value, handleChange }"
                        name="date"
                    >
                        <FormItem class="space-y-0 [&>svg]:hidden inline-flex w-full flex-col">
                            <Popover 
                                v-model:open="calendarOpen"
                                @update:open="(open: boolean) => open && inquiryAnalytics.trackFieldFocus('date')"
                            >
                                <FormControl>
                                    <PopoverTrigger as-child>
                                        <ThemeButton
                                            size="lg"
                                            class="px-3 w-full h-11 justify-between gap-4 bg-inquiry-form-item-background text-inquiry-form-text hover:bg-inquiry-form-item-hover"
                                        >
                                            <div class="w-full flex items-center gap-4">
                                                <CalendarIcon class="size-4 text-inquiry-form-text" />
                                                <span :class="cn('capsize', value ? 'text-inquiry-form-text' : 'text-inquiry-form-placeholder')">{{ value ? formatDate(value) : 'Select Date' }}</span>
                                            </div>
                                            <ChevronDown class="size-4 text-inquiry-form-text/50" />
                                        </ThemeButton>
                                    </PopoverTrigger>
                                </FormControl>
                                <PopoverContent class="p-0 border-none">
                                    <Calendar
                                        :min-value="calendarMinDate"
                                        :max-value="currentDate.add({ days: calendarRange })"
                                        :model-value="selectedDate"
                                        :is-date-disabled="isDateDisabled"
                                        class="inquiry-calendar"
                                        @update:model-value="handleChange($event?.toString()); calendarOpen = false"
                                    />
                                </PopoverContent>
                            </Popover>
                            <FormMessage class="w-full text-center" />
                        </FormItem>
                    </FormField>
                    <FormField
                        v-slot="{ value, handleChange }"
                        name="start_time"
                    >
                        <FormItem class="space-y-0 [&>svg]:hidden inline-flex w-full flex-col">
                            <Select
                                :model-value="value"
                                @update:model-value="handleChange"
                                @update:open="(open: boolean) => open && inquiryAnalytics.trackFieldFocus('start_time')"
                            >
                                <FormControl>
                                    <SelectTrigger
                                        class="border-none focus:ring-0 focus:ring-offset-0"
                                        as-child
                                    >
                                        <ThemeButton
                                            size="lg"
                                            class="h-11 justify-between gap-4 bg-inquiry-form-item-background text-inquiry-form-text hover:bg-inquiry-form-item-hover"
                                        >
                                            <div class="w-full flex items-center gap-4">
                                                <Clock class="size-4 text-inquiry-form-text" />
                                                <SelectValue
                                                    placeholder="Select Time"
                                                    class="data-[placeholder]:text-inquiry-form-placeholder capsize"
                                                />
                                            </div>
                                            <ChevronDown class="size-4 text-inquiry-form-text/50" />
                                        </ThemeButton>
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent class="max-h-64 border-none">
                                    <SelectGroup>
                                        <SelectItem
                                            v-for="o of timeOptions"
                                            :key="o.toString()"
                                            :value="o.toString()"
                                        >
                                            {{ o ? formatTime(o.toString()) : formatTime(currentTime.toString()) }}
                                        </SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <FormMessage class="w-full text-center" />
                        </FormItem>
                    </FormField>
                    <Collapsible
                        :open="true"
                        class="w-full col-span-1 md:col-span-3"
                    >
                        <CollapsibleContent class="text-sm text-center text-balance p-4 bg-inquiry-collapsible-background text-inquiry-collapsible-text">
                            We accept inquiries up to 30 days in advance.
                        </CollapsibleContent>
                    </Collapsible>
                </div>
                <Collapsible
                    v-if="callRestaurantMessage"
                    :open="!!callRestaurantMessage"
                    class="w-full"
                >
                    <CollapsibleContent class="text-sm text-center text-balance p-4 bg-inquiry-collapsible-background text-inquiry-collapsible-text">
                        {{ callRestaurantMessage }}
                    </CollapsibleContent>
                </Collapsible>
                <FormField
                    v-if="type === InquiryType.Catering"
                    v-slot="{ value, handleChange }"
                    type="radio"
                    name="style"
                >
                    <FormItem class="w-full">
                        <FormControl>
                            <RadioGroup
                                class="grid grid-cols-2 gap-2"
                                :value="value"
                                @update:model-value="handleChange"
                            >
                                <FormItem
                                    v-for="o of StyleOptions"
                                    :key="o.value"
                                    class="flex items-center space-y-0 gap-x-3"
                                >
                                    <FormControl>
                                        <RadioGroupItem
                                            :value="o.value"
                                            class="hidden"
                                        />
                                    </FormControl>
                                    <FormLabel :class="cn(buttonVariants({ size: 'lg' }), 'w-full cursor-pointer font-button bg-inquiry-form-item-background text-inquiry-form-text hover:bg-inquiry-form-item-hover transition-all', value === o.value && 'ring-2 ring-inquiry-form-text')">
                                        <span class="capsize">{{ titleCase(o.label) }}</span>
                                    </FormLabel>
                                </FormItem>
                            </RadioGroup>
                        </FormControl>
                    </FormItem>
                </FormField>
                <div
                    v-if="type === InquiryType.LargeParty && spaceOptions.length > 0"
                    class="w-full grid gap-2"
                >
                    <FormField
                        v-slot="{ value, handleChange }"
                        type="checkbox"
                        name="space"
                    >
                        <FormItem class="bg-inquiry-form-item-background space-y-6 rounded p-4">
                            <div class="space-y-0.5">
                                <FormLabel class="uppercase text-inquiry-form-text">
                                    Interested in a private space?
                                </FormLabel>
                                <FormDescription class="text-inquiry-form-description">
                                    Pricing and availability will be provided upon request.
                                </FormDescription>
                            </div>
                            <div class="flex items-center gap-3">
                                <FormControl>
                                    <Checkbox
                                        :model-value="value === 'Yes'"
                                        class="rounded border-inquiry-form-checkbox-border transition-all data-[state=checked]:bg-inquiry-form-checkbox-checked"
                                        @update:model-value="handleChange($event ? 'Yes' : 'No')"
                                    />
                                </FormControl>
                                <FormLabel class="cursor-pointer text-sm capsize text-inquiry-form-text">
                                    Yes, I'd like to learn more about private space options for my party.
                                </FormLabel>
                            </div>
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <div
                    v-else-if="type === InquiryType.PrivateDining && spaceOptions.length > 0"
                    class="w-full grid gap-2"
                >
                    <FormField name="space">
                        <FormItem class="bg-inquiry-form-item-background space-y-6 rounded p-4">
                            <div class="space-y-0.5">
                                <FormLabel class="uppercase text-inquiry-form-text">
                                    Select a Space
                                </FormLabel>
                                <FormDescription class="text-inquiry-form-description">
                                    Pricing and availability will be provided upon request.
                                </FormDescription>
                            </div>
                            <div class="space-y-2">
                                <FormField
                                    v-for="o of spaceOptions"
                                    v-slot="{ value, handleChange }"
                                    :key="o.title"
                                    type="checkbox"
                                    :value="o.title"
                                    :unchecked-value="''"
                                    name="space"
                                >
                                    <FormItem> 
                                        <FormLabel :class="cn('flex items-center gap-4 space-y-0 p-4 bg-inquiry-form-menu-background rounded transition-all', selectedGuests !== undefined && selectedGuests > (o.maxGuests || 0) ? 'cursor-not-allowed opacity-50 bg-inquiry-form-menu-background/40' : 'cursor-pointer hover:bg-inquiry-form-item-hover')">
                                            <FormControl>
                                                <Checkbox
                                                    :model-value="value === o.title"
                                                    :disabled="selectedGuests !== undefined && selectedGuests > (o.maxGuests || 0)"
                                                    :class="cn('rounded border-inquiry-form-checkbox-border transition-all data-[state=checked]:bg-inquiry-form-checkbox-checked data-[state=checked]:text-inquiry-form-submit-text', selectedGuests !== undefined && selectedGuests > (o.maxGuests || 0) && 'opacity-30')"
                                                    @update:model-value="handleChange($event || undefined)"
                                                />
                                            </FormControl>
                                            <div class="flex-1 flex items-center justify-between gap-4">
                                                <span :class="cn('font-button font-medium text-sm capsize text-inquiry-form-text')">{{ o.title }}</span>
                                                <template v-if="o.maxGuests !== Infinity">
                                                    <Badge :class="cn('shrink-0 h-auto w-40 flex items-center justify-center gap-2 font-button rounded backdrop-blur-sm', selectedGuests !== undefined && selectedGuests > (o.maxGuests || 0) ? 'bg-inquiry-form-badge-background/40 text-inquiry-form-badge-text/50 hover:bg-inquiry-form-badge-background/40' : 'bg-inquiry-form-badge-background text-inquiry-form-badge-text hover:bg-inquiry-form-badge-background/80')">
                                                        <UsersRound class="size-3" />
                                                        <span
                                                            class="capsize"
                                                        >{{ formatGuestCount(o) }}</span>
                                                    </Badge>
                                                </template>
                                            </div>
                                        </FormLabel>
                                    </FormItem>
                                </FormField>
                            </div> 
                            <FormMessage />
                        </FormItem>
                    </FormField>
                </div>
                <div
                    v-if="type === InquiryType.Catering"
                    class="w-full p-4 bg-inquiry-form-item-background rounded-lg flex flex-col gap-4"
                >
                    <div class="flex flex-col gap-1">
                        <span class="text-sm uppercase text-inquiry-form-description">Available Menus</span>
                    </div>
                    <div class="flex gap-4">
                        <ThemeButton
                            v-for="menu of location.menus.catering"
                            :key="menu.name"
                            type="button"
                            variant="outline"
                            size="sm"
                            class="flex-1 bg-inquiry-form-menu-background text-inquiry-form-text hover:bg-inquiry-form-item-hover"
                            @click="menu.path && openUrl(menu.path)"
                        >
                            {{ specialCase(menu.name, 'title') }}
                        </ThemeButton>
                    </div>
                </div>
                <div
                    v-else-if="selectedService && selectedServiceMenu && selectedGuests"
                    class="w-full p-4 bg-inquiry-form-item-background rounded-lg flex flex-col gap-6"
                >
                    <div class="flex flex-col gap-0.5">
                        <span class="text-sm uppercase text-inquiry-form-text">Available Menu</span>
                        <span class="text-sm text-inquiry-form-description">{{ selectedServiceMenuMessage }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-4">
                        <div class="flex items-center gap-4">
                            <div class="size-9 grid place-items-center rounded-full bg-inquiry-form-menu-background">
                                <Utensils class="size-4 text-inquiry-form-text" />
                            </div>
                            <span class="text-sm font-button font-medium capsize text-inquiry-form-text">{{ titleCase(selectedServiceMenu.name) }}</span>
                        </div> 
                        <ThemeButton
                            type="button"
                            variant="outline"
                            size="sm"
                            class="bg-transparent text-inquiry-form-text border-inquiry-form-text hover:bg-inquiry-form-checkbox-checked hover:text-inquiry-form-submit-text"
                            @click="selectedServiceMenu.path && openUrl(selectedServiceMenu.path)"
                        >
                            <span>View Menu</span>
                        </ThemeButton>
                    </div>
                </div>
                <FormField
                    v-slot="{ componentField }"
                    name="description"
                >
                    <FormItem class="w-full">
                        <FormLabel class="uppercase text-inquiry-form-text">
                            Event Description
                        </FormLabel>
                        <FormControl>
                            <ThemeTextarea
                                :maxlength="descriptionLimit"
                                placeholder="Tell us what you have in mind and any special requests..."
                                class="bg-inquiry-form-item-background text-inquiry-form-text [&::placeholder]:text-inquiry-form-placeholder"
                                v-bind="componentField"
                                @focus="inquiryAnalytics.trackFieldFocus('description')"
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
                <div class="w-full bg-inquiry-form-menu-background p-4 space-y-3">
                    <header class="space-y-2">
                        <h4 class="uppercase text-sm text-inquiry-form-text">
                            Need to know
                        </h4>
                        <Separator class="w-10 bg-inquiry-form-text/20" />
                    </header>
                    <ul class="text-sm space-y-0.5 text-inquiry-form-text">
                        <li
                            v-for="li of needToKnow"
                            :key="li"
                        >
                            {{ li }}
                        </li>
                    </ul>
                </div>
                <div class="w-full flex flex-col gap-x-2 gap-y-10">
                    <div class="flex flex-col gap-4">
                        <FormField
                            v-slot="{ value, handleChange }"
                            type="checkbox"
                            name="gdpr"
                        >
                            <FormItem>
                                <div class="flex items-center gap-2">
                                    <FormControl>
                                        <Checkbox
                                            :model-value="value"
                                            class="rounded bg-inquiry-form-item-background border-inquiry-form-checkbox-border data-[state=checked]:bg-inquiry-form-checkbox-checked"
                                            @update:model-value="handleChange"
                                        />
                                    </FormControl>
                                    <FormLabel class="text-inquiry-form-text">
                                        Accept Tripleseat's 
                                        <a
                                            href="https://www.tripleseat.com/privacy-policy/"
                                            target="_blank"
                                            class="underline"
                                        >
                                            privacy policy
                                        </a>
                                    </FormLabel>
                                </div>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                        <FormField
                            v-slot="{ value, handleChange }"
                            type="checkbox"
                            name="inquiryAcknowledgement"
                        >
                            <FormItem>
                                <div class="flex items-center gap-2">
                                    <FormControl>
                                        <Checkbox
                                            :model-value="value"
                                            class="rounded bg-inquiry-form-item-background border-inquiry-form-checkbox-border data-[state=checked]:bg-inquiry-form-checkbox-checked"
                                            @update:model-value="handleChange"
                                        />
                                    </FormControl>
                                    <FormLabel class="text-inquiry-form-text">
                                        I acknowledge that this inquiry does not guarantee a reservation.
                                    </FormLabel>
                                </div>
                                <FormMessage />
                            </FormItem>
                        </FormField>
                    </div>
                    <div 
                        v-if="Object.keys(form.errors.value).length"
                        role="alert"
                        aria-live="assertive"
                        class="bg-destructive/10 border border-destructive/30 p-4 rounded-none w-full"
                    >
                        <h2 class="text-destructive font-semibold mb-2">
                            Please correct the following errors:
                        </h2>
                        <ul class="list-disc pl-5">
                            <li 
                                v-for="(error, index) in form.errors.value"
                                :key="index"
                                class="text-destructive"
                            >
                                {{ error }}
                            </li>
                        </ul>
                    </div>
                    <ThemeButton
                        :loading="createLeadLoading"
                        :disabled="createLeadLoading || Object.keys(form.errors.value).length > 0"
                        type="submit"
                        size="lg"
                        class="w-full bg-inquiry-form-submit-background text-inquiry-form-submit-text hover:bg-inquiry-form-submit-background/80"
                    >
                        Send Inquiry
                    </ThemeButton>
                </div>
            </form>
            <div
                v-else
                class="w-full flex flex-col gap-4 items-center"
            >
                <div
                    class="space-y-4 text-inquiry-form-text"
                    v-html="createdLead?.message"
                />
                <ThemeButton
                    class="bg-inquiry-form-submit text-inquiry-form-submit-text hover:bg-inquiry-form-submit/80 min-w-40"
                    @click="open = false; emit('success')"
                >
                    Close
                </ThemeButton>
            </div>
        </div>
    </InquiryFormTemplate>
    <Dialog 
        v-if="isDesktop"
        v-model:open="open"
    >
        <DialogTrigger as-child>
            <slot />
        </DialogTrigger>
        <DialogContent
            :class="cn('size-full max-w-none bg-inquiry-dialog-background text-inquiry-form-text sm:rounded-none border-none p-0 [&>button:focus-visible]:outline-card-foreground', cl)"
            @pointer-down-outside="e => {
                if ((e.target as Element).closest('[data-sonner-toast]')) {
                    e.preventDefault();
                }
            }"
        >
            <div class="size-full max-h-screen overflow-y-auto">
                <div class="max-w-2xl mx-auto px-6 py-12">
                    <InquiryForm
                        :type="type"
                        :location="location"
                        :guests="guests"
                        :date="date"
                        :time="time"
                    />
                </div>
            </div>
        </DialogContent>
    </Dialog>
    <Drawer
        v-else
        v-model:open="open"
    >
        <DrawerTrigger as-child>
            <slot />
        </DrawerTrigger>
        <DrawerContent
            :class="cn('max-h-[95svh] [&>:first-child]:shrink-0 bg-inquiry-dialog-background text-inquiry-form-text border-none', cl)"
        >
            <DrawerClose
                class="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Close dialog"
            >
                <X class="size-4" />
            </DrawerClose>
            <div class="size-full max-h-full overflow-y-auto p-4">
                <InquiryForm
                    :type="type"
                    :location="location"
                    :guests="guests"
                    :date="date"
                    :time="time"
                />
            </div>
        </DrawerContent>
    </Drawer>
</template>

<style scoped>
.inquiry-calendar {
    --muted-foreground: var(--inquiry-form-calendar-text);
}
</style>